import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n, formatDateShort } from "@/lib/i18n";

export default function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, locale } = useI18n();

  useEffect(() => {
    api
      .get("/events", { params: { only_public: true } })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="eventos"
      data-testid="upcoming-events-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div>
            <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4">{t.events.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">{t.events.title}</h2>
          </div>
          <p className="text-[#5C544F] max-w-md text-base leading-relaxed">{t.events.desc}</p>
        </motion.div>

        {loading ? (
          <div className="text-[#5C544F]" data-testid="events-loading">…</div>
        ) : events.length === 0 ? (
          <div
            className="py-16 text-center text-[#5C544F] border border-dashed border-[#eae0df] rounded-2xl"
            data-testid="events-empty"
          >
            {t.events.empty}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="upcoming-events-grid">
            {events.map((ev, idx) => (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                whileHover={{ y: -6 }}
                className="group bg-white rounded-2xl border border-[#eae0df] overflow-hidden shadow-[0_8px_30px_rgb(42,36,33,0.04)] hover:shadow-[0_20px_40px_rgb(42,36,33,0.10)] transition-shadow"
                data-testid={`event-card-${ev.id}`}
              >
                <Link
                  to={`/eventos/${ev.id}`}
                  onClick={() => {
                    try { sessionStorage.setItem("aura.homeScroll", String(window.scrollY)); } catch { /* ignore */ }
                  }}
                  className="block"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#1c2421]">
                    {ev.cover_video_url ? (
                      <video
                        src={ev.cover_video_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        data-testid={`event-card-video-${ev.id}`}
                      />
                    ) : ev.image_url ? (
                      <img
                        src={ev.image_url}
                        alt={ev.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : null}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3 text-xs uppercase tracking-[0.2em] text-[#5C544F]">
                      <span data-testid={`event-date-${ev.id}`}>{formatDateShort(ev.date, locale)}</span>
                      <span className="px-2 py-1 rounded-full bg-[#3e664c]/10 text-[#3e664c]">{t.events.publicTag}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-[#2A2421] mb-3">
                      {ev.title}
                    </h3>
                    <p className="text-sm text-[#5C544F] line-clamp-2 leading-relaxed">{ev.description}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-[#3e664c] text-sm font-medium">
                        {ev.ticket_price ? `${ev.ticket_price.toFixed(2)} €` : t.events.ticket}
                      </span>
                      <span className="text-sm text-[#2A2421] group-hover:text-[#3e664c] transition-colors">
                        {t.events.seeDetail}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA banner — below events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 rounded-2xl border border-[#3e664c]/20 bg-gradient-to-r from-[#3e664c]/5 to-transparent p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          data-testid="events-cta-banner"
        >
          <p className="font-display text-xl md:text-2xl tracking-tight max-w-2xl">
            {t.events.ctaBanner}
          </p>
          <a
            href="#disponibilidad"
            data-testid="events-cta-calendar"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] text-sm uppercase tracking-[0.18em] transition-colors"
          >
            {t.cta.pickDate}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
